import React, { useState, useEffect } from 'react';
import { FaLeaf, FaBolt, FaUsers, FaHandshake } from 'react-icons/fa';
import { Link } from 'react-router-dom';

// A custom hook to get the window size for responsive inline styles
const useWindowSize = () => {
  const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
  useEffect(() => {
    const handleResize = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return size;
};


// ========== The Main Component Starts Here ==========
const GreenEnergyPage = () => {
  const [width] = useWindowSize();
  const isMobile = width < 768;

  // State to manage hover effects
  const [hoveredValue, setHoveredValue] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hoveredClient, setHoveredClient] = useState(null);

  // ========== INLINE CSS STYLES OBJECT ==========
  const styles = {
    page: {
      fontFamily: "'Poppins', sans-serif",
      color: '#333',
    },
    heroSection: {
      position: 'relative',
    
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      textAlign: 'center',
    },
    heroOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
    },
    heroContent: {
      position: 'relative',
      zIndex: 1,
    },
    heroTitle: {
      fontSize: isMobile ? '2rem' : '3rem',
      fontWeight: 'bolder',
            textShadow: '2px 2px 8px rgba(0,0,0,0.5)',

    },
    section: {
      padding: '80px 0',
    },
    sectionLight: {
      padding: '80px 0',
      backgroundColor: '#f8f9fa',
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: '60px',
    },
    sectionTitle: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
    },
    sectionSubtitle: {
      textTransform: 'uppercase',
      fontWeight: 'bold',
      color: '#28a745',
      letterSpacing: '2px',
    },
    infoImage: {
      border: '10px solid white',
      transform: 'rotate(2deg)',
      borderRadius: '8px',
      boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
    },
    infoImageAlt: {
        border: '10px solid white',
        transform: 'rotate(-2deg)',
        borderRadius: '8px',
        boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
    },
    valueCard: {
      textAlign: 'center',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      border: 'none',
      boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    },
    valueCardHover: {
      transform: 'translateY(-10px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    },
    segmentCard: {
      height: '350px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: '16px',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'flex-end',
      padding: '1.5rem',
      color: 'white',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    },
    segmentCardHover: {
      transform: 'translateY(-10px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    },
    segmentCardOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.1))',
    },
    segmentCardTitle: {
      position: 'relative',
      zIndex: 1,
      fontWeight: 'bold',
      fontSize: '1.75rem',
      color: 'white',
    },
    mdImage: {
      border: '10px solid white',
      transform: 'rotate(2deg)',
      borderRadius: '8px',
      boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
    },
    clientLogoContainer: {
        padding: '1rem',
        cursor: 'pointer',
    },
    clientLogo: {
      maxHeight: '60px',
      width: '100%',
      objectFit: 'contain',
      filter: 'grayscale(100%)',
      opacity: 0.7,
      transition: 'all 0.3s ease',
    },
    clientLogoHover: {
      filter: 'grayscale(0%)',
      opacity: 1,
      transform: 'scale(1.1)',
    },
    ctaSection: {
      position: 'relative',
      padding: '100px 0',
      color: 'white',
      textAlign: 'center',
    },
    ctaOverlay: {
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#23A13E',
      clipPath: 'polygon(0 15%, 100% 0, 100% 85%, 0 100%)',
    },
    ctaButton: {
      borderRadius: '30px',
      padding: '15px 40px',
      fontWeight: 'bold',
      textDecoration: 'none',
      color: '#333',
      backgroundColor: 'white',
      boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
    },
  };

  // --- Data Arrays ---
  const values = [
    { icon: <FaLeaf style={{ fontSize: '3rem', marginBottom: '1rem', color: '#28a745' }} />, title: "Transparency" },
    { icon: <FaUsers style={{ fontSize: '3rem', marginBottom: '1rem', color: '#28a745' }} />, title: "Adaptability" },
    { icon: <FaHandshake style={{ fontSize: '3rem', marginBottom: '1rem', color: '#28a745' }} />, title: "Dependability" },
    { icon: <FaBolt style={{ fontSize: '3rem', marginBottom: '1rem', color: '#28a745' }} />, title: "Longevity" },
  ];
  const segments = [
    { title: "Residential Solar System", img: "/assets/img/bg/abc.jpg" },
    { title: "Commercial Solar System", img: "/assets/img/bg/abcd.jpg" },
    { title: "Industrial Solar System", img: "/assets/img/bg/abcde.jpg" }
  ];
 const clientLogos = [
'/assets/img/bg/Poojara-logo-1753099967449.png', '/assets/img/bg/Reliance_Digital.svg.png', '/assets/img/bg/logo.svg',
'/assets/img/bg/Logosss.webp', '/assets/img/bg/Babulime.png', '/assets/img/bg/Tata_Motors_Logo.svg.png',
];

  return (
    <div style={{overflow:"hidden"}}>
    
      <section style={styles.heroSection}>
        <div style={styles.heroOverlay}></div>
        <div className="container" style={styles.heroContent}>
          <h1 className='py-3 tp-ab-title' >SOLAR ENERGY</h1>
          <h2 className="display-4 ">FOR YOUR HOME & BUSINESS</h2>
          <p className="lead fs-3 mt-4">CLEAN ENERGY FOR BETTER TOMORROW.</p>
        </div>
      </section>

      {/* ========== ABOUT US SECTION (IMAGE LEFT, CONTENT RIGHT) ========== */}
      <section style={styles.section}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <img src="/assets/img/bg/360_F_323659172_qAAbGT4OE9UTELLC87MZqHKfurkYWg8C.jpg" className="img-fluid" style={styles.infoImage} alt="Our Team at a Solar Farm" />
            </div>
            <div className="col-lg-6">
              <h6 style={{color:"#878787"}} className='tp-ab-subtitle'>Who We Are</h6>
              <h2 className='tp-ab-title'>About Us</h2>
              <p className="tp-ab-info">LUMETIQ GREEN ENERGY is a pioneer in turnkey Solar PV Power Project Company dedicated to accelerating India's transition to sustainable energy. Established in 2018, with our Corporate Headquarters strategically located in Rajkot, Gujarat, we leverage our deep expertise to deliver comprehensive solar solutions across various scales.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION SECTION (CONTENT LEFT, IMAGE RIGHT) ========== */}
      <section style={styles.sectionLight}>
        <div className="container">
          <div className="row align-items-center g-5">
            {/* --- Text content comes first in the code --- */}
            <div className="col-lg-6">
              <h6 style={{color:"#878787"}} className='tp-ab-subtitle'>Our Purpose</h6>
              <h2 className='tp-ab-title'>Vision & Mission</h2>
              <p className="tp-ab-info">LUMETIQ GREEN ENERGY is dedicated to designing and delivering high-quality, affordable, and easily accessible solar power and solar thermal solutions. We empower domestic, commercial, and industrial customers to significantly reduce costs associated with conventional, more expensive energy sources, thereby fostering a sustainable and greener future for all.</p>
            </div>
            {/* --- Image comes second, so it will appear on the right --- */}
            <div className="col-lg-6">
              <img src="/assets/img/bg/photo-1497440001374-f26997328c1b.jpg" className="img-fluid" style={styles.infoImageAlt} alt="Blueprint of a Solar Project" />
            </div>
          </div>
        </div>
      </section>
      
      {/* ========== OUR VALUES SECTION ========== */}
      <section style={styles.section}>
        <div className="container">
            <div style={styles.sectionHeader}>
                <h6 style={{color:"#878787"}} className='tp-ab-subtitle'>Our Principles</h6>
                <h2 className='tp-ab-title'>Our Core Values</h2>
            </div>
            <div className="row g-4">
                {values.map((value, index) => (
                    <div key={index} className="col-md-6 col-lg-3">
                        <div 
                            style={{ ...styles.valueCard, ...(hoveredValue === index ? styles.valueCardHover : {}) }}
                            onMouseEnter={() => setHoveredValue(index)}
                            onMouseLeave={() => setHoveredValue(null)}
                        >
                            {value.icon}
                            <h4 className="fw-bold mb-3">{value.title}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* ========== BUSINESS SEGMENTS SECTION ========== */}
      <section style={styles.sectionLight}>
        <div className="container">
            <div style={styles.sectionHeader}>
                <h6 style={{color:"#878787"}} className='tp-ab-subtitle'>What We Do</h6>
                <h2 className='tp-ab-title'>Business Segments</h2>
            </div>
            <div className="row g-4">
                {segments.map((seg, i) => (
                <div key={i} className="col-lg-4">
                    <div 
                    style={{ ...styles.segmentCard, backgroundImage: `url(${seg.img})`, ...(hoveredSegment === i ? styles.segmentCardHover : {}) }}
                    onMouseEnter={() => setHoveredSegment(i)}
                    onMouseLeave={() => setHoveredSegment(null)}
                    >
                    <div style={styles.segmentCardOverlay}></div>
                    <h5 style={styles.segmentCardTitle}>{seg.title}</h5>
                    </div>
                </div>
                ))}
            </div>
        </div>
      </section>
      
      {/* ========== MD'S DESK SECTION ========== */}
      <section style={styles.section}>
         <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <h6 style={{color:"#878787"}} className='tp-ab-subtitle'>Leadership</h6>
              <h2 className='tp-ab-title'>From The MD's Desk</h2>
              <p style={{color:"#878787"}} className="tp-ab-info">"Our visionary leadership and unwavering commitment have propelled us to become a key player in the renewable energy sector. We are dedicated to delivering innovative expertise for a greener tomorrow."</p>
            </div>
            <div className="col-lg-6 text-center">
              <img src="/assets/img/bg/Screenshot 2025-09-01 171026.png" className="img-fluid" style={styles.mdImage} alt="Managing Director" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== CLIENTS SECTION ========== */}
      <section style={styles.sectionLight}>
        <div className="container">
            <div style={styles.sectionHeader}>
                <h6 style={{color:"#878787"}} className='tp-ab-subtitle'>Our Partners</h6>
                <h2 className='tp-ab-title'>Our Esteemed Clients</h2>
            </div>
            <div className="row g-4 align-items-center">
                {clientLogos.map((logo, index) => (
                    <div key={index} className="col-6 col-md-4 col-lg-2">
                        <div
                        style={styles.clientLogoContainer}
                        onMouseEnter={() => setHoveredClient(index)}
                        onMouseLeave={() => setHoveredClient(null)}
                        >
                        <img
                            src={logo}
                            className="img-fluid"
                            alt={`Client Logo ${index + 1}`}
                            style={{
                            ...styles.clientLogo,
                            ...(hoveredClient === index ? styles.clientLogoHover : {})
                            }}
                        />
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </section>
      
      {/* ========== CTA SECTION ========== */}
      <section style={styles.ctaSection}>
        <div style={styles.ctaOverlay}></div>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <h2 className="tp-ab-title">Ready to Go Solar?</h2>
                    <p style={{color:"white"}} className="mt-5 tp-ab-info">Join us in building a sustainable future. Explore our solutions and find the perfect fit for your energy needs.</p>
                    <Link to="/contact" style={styles.ctaButton}>Get a Free Quote</Link>
                </div>
            </div>
        </div>
      </section>
    </div>
  );
};

export default GreenEnergyPage;